import React from 'react';
import { motion } from 'framer-motion';
import { Check, X } from 'lucide-react';
import { COMPARISON_ROWS } from '../constants';

export const ComparativaSection: React.FC = () => {
  const isYes = (val: string) =>
    val === 'Sí' || val === 'Incluido' || val === 'Inmediata' || val === 'Automático' || val === '24/7/365' || val === 'Hasta 7 (diario)' || val === 'Desde 9,99€';

  const isNo = (val: string) => val === 'No' || val === 'No disponible';

  return (
    <section id="comparativa" className="py-24 bg-slate-950 text-white relative overflow-hidden dark-noise">
      {/* Background depth */}
      <div className="absolute inset-0 bg-gradient-to-b from-slate-950 via-slate-900/20 to-slate-950 pointer-events-none" />

      <div className="container mx-auto px-4 max-w-7xl relative z-10">
        {/* Hero image */}
        <div className="relative rounded-2xl overflow-hidden mb-16">
          <img
            src="/equipo-colaborando.jpg"
            alt=""
            className="w-full h-[220px] md:h-[320px] object-cover"
            style={{ objectPosition: '50% 40%' }}
            loading="lazy"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950 via-slate-950/70 to-slate-950/40" />

          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
            <motion.span
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="inline-flex items-center gap-2 py-1 px-4 rounded-full text-sm font-bold mb-5 uppercase tracking-widest"
              style={{
                background: 'rgba(60,179,113,0.12)',
                border: '1px solid rgba(60,179,113,0.35)',
                color: '#7ee8a2',
              }}
            >
              Comparativa
            </motion.span>

            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
              className="font-black text-white mb-2"
              style={{ fontSize: 'clamp(2rem, 5vw, 3rem)', letterSpacing: '-0.02em' }}
            >
              Ancora vs las alternativas
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.2 }}
              className="text-slate-400 font-light"
            >
              El mismo resultado (o mejor), a una fracción del coste
            </motion.p>
          </div>
        </div>
        {/* Desktop table */}
        <div className="hidden md:block overflow-x-auto rounded-2xl" style={{ border: '1px solid rgba(255,255,255,0.06)' }}>
          <table className="w-full border-collapse">
            <thead>
              <tr style={{ background: 'rgba(255,255,255,0.03)' }}>
                <th className="text-left py-5 px-5 text-slate-400 font-medium text-xs uppercase tracking-widest w-1/5"></th>
                {["Hacerlo tú", "Community Manager", "Agencia Marketing"].map((col) => (
                  <th key={col} className="py-5 px-5 text-center text-slate-400 font-medium text-sm">{col}</th>
                ))}
                <th className="py-5 px-5 text-center">
                    <div className="inline-block">
                    <div
                      className="px-5 py-2 rounded-xl inline-block relative"
                      style={{
                        background: 'rgba(60,179,113,0.12)',
                        border: '1px solid rgba(60,179,113,0.35)',
                        boxShadow: '0 0 20px rgba(60,179,113,0.1)',
                      }}
                    >
                      <span
                        className="font-black text-base"
                        style={{ color: '#3CB371' }}
                      >
                        Ancora
                      </span>
                      <span
                        className="absolute -top-3 left-1/2 -translate-x-1/2 text-[10px] font-black px-2 py-0.5 rounded-full whitespace-nowrap text-white uppercase tracking-widest"
                        style={{ background: 'linear-gradient(135deg, #3CB371, #1a8a52)' }}
                      >
                        Recomendado
                      </span>
                    </div>
                  </div>
                </th>
              </tr>
            </thead>
            <tbody>
              {COMPARISON_ROWS.map((row, i) => (
                <tr
                  key={i}
                  className="comparison-row border-t"
                  style={{ borderColor: 'rgba(255,255,255,0.05)', background: i % 2 === 0 ? 'rgba(255,255,255,0.015)' : 'transparent' }}
                >
                  <td className="py-4 px-5 text-slate-300 font-medium text-sm">{row.feature}</td>
                  <td className="py-4 px-5 text-center text-sm text-slate-500">{row.self}</td>
                  <td className="py-4 px-5 text-center text-sm text-slate-500">{row.cm}</td>
                  <td className="py-4 px-5 text-center text-sm text-slate-500">{row.agency}</td>
                  <td
                    className="py-4 px-5 text-center"
                    style={{
                      background: 'rgba(60,179,113,0.06)',
                      borderLeft: '1px solid rgba(60,179,113,0.15)',
                    }}
                  >
                    {isYes(row.ancora) ? (
                      <span
                        className="inline-flex items-center gap-1.5 font-bold text-sm"
                        style={{ color: '#3CB371' }}
                      >
                        <Check size={15} strokeWidth={3} />
                        {row.ancora}
                      </span>
                    ) : isNo(row.ancora) ? (
                      <span className="inline-flex items-center gap-1.5 text-sm" style={{ color: '#94a3b8' }}>
                        <X size={15} />
                        {row.ancora}
                      </span>
                    ) : (
                      <span className="font-bold text-sm" style={{ color: '#3CB371' }}>{row.ancora}</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile: cards */}
        <div className="md:hidden space-y-3">
          {COMPARISON_ROWS.map((row, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 10 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
              className="rounded-xl p-4"
              style={{
                background: 'rgba(255,255,255,0.04)',
                border: '1px solid rgba(255,255,255,0.07)',
              }}
            >
              <div className="font-bold text-white mb-3 text-sm">{row.feature}</div>
              <div className="grid grid-cols-2 gap-2 text-xs">
                <div className="rounded-lg p-2.5" style={{ background: 'rgba(255,255,255,0.05)' }}>
                  <div className="text-slate-500 mb-1 text-[10px] uppercase tracking-wide">Hacerlo tú</div>
                  <div className="text-slate-300">{row.self}</div>
                </div>
                <div className="rounded-lg p-2.5" style={{ background: 'rgba(255,255,255,0.05)' }}>
                  <div className="text-slate-500 mb-1 text-[10px] uppercase tracking-wide">Comm. Manager</div>
                  <div className="text-slate-300">{row.cm}</div>
                </div>
                <div className="rounded-lg p-2.5" style={{ background: 'rgba(255,255,255,0.05)' }}>
                  <div className="text-slate-500 mb-1 text-[10px] uppercase tracking-wide">Agencia</div>
                  <div className="text-slate-300">{row.agency}</div>
                </div>
                <div
                  className="rounded-lg p-2.5"
                  style={{
                    background: 'rgba(60,179,113,0.1)',
                    border: '1px solid rgba(60,179,113,0.25)',
                  }}
                >
                  <div className="mb-1 font-bold text-[10px] uppercase tracking-wide" style={{ color: '#3CB371' }}>Ancora</div>
                  {isYes(row.ancora) ? (
                    <div className="font-bold flex items-center gap-1" style={{ color: '#3CB371' }}>
                      <Check size={11} strokeWidth={3} />
                      {row.ancora}
                    </div>
                  ) : (
                    <div className="font-bold" style={{ color: '#3CB371' }}>{row.ancora}</div>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <button
            onClick={() => document.getElementById('pricing')?.scrollIntoView({ behavior: 'smooth' })}
            className="inline-flex items-center gap-2 font-bold px-8 py-3.5 rounded-full transition-all duration-300 hover:-translate-y-1 text-slate-900"
            style={{
              background: 'linear-gradient(135deg, #3CB371, #2d8653)',
              boxShadow: '0 0 25px rgba(60,179,113,0.3)',
            }}
          >
            Ver planes desde 9,99€/mes
          </button>
        </motion.div>
      </div>
    </section>
  );
};
